import { useState } from "react";
import SectionLabel from "@/components/SectionLabel";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  Cross,
  Droplets,
  Heart,
  HandHeart,
  Flower,
  Sparkles,
  MoreHorizontal,
  Info,
  CheckCircle2,
} from "lucide-react";

const TYPES = [
  {
    id: "pastoral_interview",
    label: "Pastoral Interview",
    sub: "Counsel · Discernment · Concerns",
    icon: Users,
    docs: [
      "A short note describing the topic or concern you wish to discuss.",
      "Valid ID (if first-time visitor).",
    ],
  },
  {
    id: "holy_mass",
    label: "Holy Mass",
    sub: "Personal · Family · Community Mass",
    icon: Cross,
    docs: [
      "Names of the celebrant / family / community.",
      "Mass intention (e.g., thanksgiving, birthday, anniversary).",
      "Preferred venue (Parish Church or GKK chapel).",
    ],
  },
  {
    id: "baptism",
    label: "Baptism",
    sub: "Sundays · 9:30 AM",
    icon: Droplets,
    docs: [
      "GKK Certification signed by the Unit President & noted by the GKK President.",
      "Child's Birth Certificate (photocopy).",
      "Parents' Marriage Contract — if married Catholic (photocopy).",
      "Mother's Baptismal Certificate — if not married (photocopy).",
      "Death Certificate — if one parent has passed (photocopy).",
    ],
  },
  {
    id: "wedding",
    label: "Wedding",
    sub: "Saturdays · 7:30 AM · 2-month preparation",
    icon: Heart,
    docs: [
      "Wedding Application Form.",
      "Baptismal Certificate (newly issued, original).",
      "Confirmation Certificate (newly issued, original).",
      "Permit to Marry / Parish Permit (if from another parish).",
      "Marriage License (City Hall, original).",
      "CENOMAR (NSO) or Death Certificate (for widow/er).",
      "Cana 101 Seminar (at GKK Chapel) — schedule after applying.",
    ],
  },
  {
    id: "anointing",
    label: "Anointing of the Sick",
    sub: "Urgent calls accepted anytime",
    icon: HandHeart,
    docs: [
      "Sick Call Form (the parish office will provide if needed).",
      "Name, age, and address of the patient.",
      "Brief description of the illness or condition.",
    ],
  },
  {
    id: "funeral",
    label: "Funeral Mass",
    sub: "Saturdays · 1:00 PM",
    icon: Flower,
    docs: [
      "Death Certificate (or proof of death from the funeral home).",
      "Baptismal & Confirmation Certificates of the deceased (if available).",
    ],
  },
  {
    id: "blessing",
    label: "Blessing",
    sub: "Home · Vehicle · Religious Articles",
    icon: Sparkles,
    docs: [
      "Address or location of the blessing.",
      "Brief description of what will be blessed.",
    ],
  },
  {
    id: "other",
    label: "Other Parish Concern",
    sub: "Anything else we can help with",
    icon: MoreHorizontal,
    docs: ["Kindly describe your concern in the purpose and notes sections below."],
  },
];

const blank = {
  full_name: "",
  contact: "",
  email: "",
  address: "",
  type: "",
  preferred_date: "",
  preferred_time: "",
  purpose: "",
  notes: "",
};

export default function Appointments() {
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const selected = TYPES.find((t) => t.id === form.type);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please enter your full name.");
    if (!form.contact.trim()) return toast.error("Please enter a contact number.");
    if (!form.type) return toast.error("Please choose an appointment type.");
    if (!form.preferred_date) return toast.error("Please choose a preferred date.");
    if (!form.purpose.trim()) return toast.error("Please describe the purpose of your request.");

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email || null,
      };
      const res = await api.post("/appointments", payload);
      setConfirmed(res.data);
      toast.success("Appointment request received — pax et bonum.");
      setForm(blank);
    } catch (err) {
      toast.error("Could not submit. Please try again or call the parish office.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="appointments" className="py-24 md:py-36 bg-parchment-200 grain relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(212,175,55,0.06),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="Walk with us"
          title="Parish Appointment Request"
          intro="Request a meeting with the parish for sacraments, pastoral concerns, or special celebrations. Our doors are always open in welcome."
        />

        {/* Disclaimer */}
        <div className="mt-12 max-w-3xl mx-auto bg-parchment-100 border-l-2 border-terracotta p-6 md:p-7 flex items-start gap-4">
          <Info className="text-terracotta mt-0.5 flex-shrink-0" size={20} strokeWidth={1.4} />
          <div className="font-sans text-cocoa-800 text-sm leading-relaxed">
            <strong className="font-medium text-cocoa-900">A gentle reminder:</strong> Submitting
            this form does <em className="text-terracotta not-italic font-medium">not</em>{" "}
            automatically confirm your appointment. The Parish Office will contact you to verify
            the details, complete any required documents, and confirm the schedule. We thank you
            for your patience and prayerful preparation.
          </div>
        </div>

        {confirmed ? (
          <ConfirmationPanel data={confirmed} onClose={() => setConfirmed(null)} />
        ) : (
          <>
            <StatusLookup />
            <div className="mt-12 grid lg:grid-cols-12 gap-8">
            {/* Type sidebar */}
            <aside className="lg:col-span-4 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-3 font-sans">
                Choose a service
              </div>
              {TYPES.map((t) => {
                const Icon = t.icon;
                const active = form.type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`appt-type-${t.id}`}
                    onClick={() => setForm((f) => ({ ...f, type: t.id }))}
                    className={`w-full text-left border bg-parchment-50 p-4 transition-all duration-300 flex gap-3 items-start ${
                      active
                        ? "border-terracotta bg-terracotta/[0.06]"
                        : "border-parchment-400 hover:border-cocoa-900/40"
                    }`}
                  >
                    <Icon
                      className={active ? "text-terracotta" : "text-cocoa-700"}
                      size={20}
                      strokeWidth={1.4}
                    />
                    <div className="flex-1">
                      <div className="font-serif text-base text-cocoa-900 leading-tight">
                        {t.label}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-dark mt-1 font-sans">
                        {t.sub}
                      </div>
                    </div>
                  </button>
                );
              })}
            </aside>

            {/* Form */}
            <div className="lg:col-span-8 bg-parchment-50 border border-parchment-400 p-8 md:p-12">
              {!selected ? (
                <div className="flex flex-col items-center justify-center text-center py-24">
                  <div className="font-serif text-3xl text-cocoa-900 italic mb-3">
                    Please choose a service
                  </div>
                  <p className="font-sans text-cocoa-700 max-w-md">
                    Select the type of appointment from the list on the left to begin your
                    request.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-7" data-testid="appointment-form">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-2 font-sans">
                      {selected.label}
                    </div>
                    <h3 className="font-serif text-3xl text-cocoa-900 leading-tight">
                      Tell us about your request.
                    </h3>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Field
                      label="Full Name"
                      required
                      value={form.full_name}
                      onChange={update("full_name")}
                      placeholder="Juan dela Cruz"
                      testId="appt-name"
                    />
                    <Field
                      label="Contact Number"
                      required
                      value={form.contact}
                      onChange={update("contact")}
                      placeholder="09XX XXX XXXX"
                      testId="appt-contact"
                    />
                    <Field
                      label="Email Address"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="name@example.com"
                      testId="appt-email"
                    />
                    <Field
                      label="Address or GKK / Mission Area"
                      value={form.address}
                      onChange={update("address")}
                      placeholder="e.g., Brgy. Mabuhay · GKK San Roque"
                      testId="appt-address"
                    />
                    <Field
                      label="Preferred Date"
                      required
                      type="date"
                      value={form.preferred_date}
                      onChange={update("preferred_date")}
                      testId="appt-date"
                    />
                    <Field
                      label="Preferred Time"
                      type="time"
                      value={form.preferred_time}
                      onChange={update("preferred_time")}
                      testId="appt-time"
                    />
                  </div>

                  <div>
                    <Label required>Purpose of Request</Label>
                    <textarea
                      data-testid="appt-purpose"
                      value={form.purpose}
                      onChange={update("purpose")}
                      rows={3}
                      placeholder="In a few sentences, kindly tell us the reason for this appointment."
                      className="field min-h-[90px] resize-y"
                    />
                  </div>

                  <div>
                    <Label>Special Notes (optional)</Label>
                    <textarea
                      data-testid="appt-notes"
                      value={form.notes}
                      onChange={update("notes")}
                      rows={3}
                      placeholder="Mobility needs, languages, family circumstances, or anything else we should know."
                      className="field min-h-[80px] resize-y"
                    />
                  </div>

                  {/* Document requirements card */}
                  {selected.docs?.length > 0 && (
                    <div className="bg-cocoa-900 text-parchment-100 p-6 md:p-7">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3 font-sans">
                        Documents to Prepare
                      </div>
                      <ul className="space-y-2.5 font-sans text-sm leading-relaxed">
                        {selected.docs.map((d, i) => (
                          <li key={i} className="flex gap-3 items-start">
                            <span className="text-gold font-serif text-sm flex-shrink-0 mt-0.5">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="text-parchment-200/90 font-light">{d}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-5 text-[10px] uppercase tracking-[0.22em] text-parchment-200/70 italic font-sans">
                        ✦ Documents are not uploaded here. Kindly bring originals & photocopies
                        to the parish office.
                      </p>
                    </div>
                  )}

                  <div className="pt-2 flex flex-wrap items-center gap-5">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="appt-submit"
                      className="inline-flex items-center gap-3 bg-cocoa-900 text-parchment-100 px-8 py-4 font-sans text-sm tracking-wide hover:bg-terracotta transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Sending…" : "Request Appointment"}
                      <span>→</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(blank)}
                      className="text-sm text-cocoa-700 hover:text-terracotta font-sans"
                    >
                      Clear
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
          </>
        )}

        <style>{`
          .field {
            width: 100%;
            background: transparent;
            border: 0;
            border-bottom: 1px solid rgba(122,92,79,0.4);
            padding: 10px 2px;
            font-family: 'Figtree', system-ui, sans-serif;
            color: #3E2723;
            outline: none;
            transition: border-color 0.2s;
          }
          .field:focus { border-bottom-color: #C25953; }
          .field::placeholder { color: rgba(62,39,35,0.3); font-style: italic; }
        `}</style>
      </div>
    </section>
  );
}

function Label({ children, required }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-1">
      {children}
      {required && <span className="text-terracotta ml-1">*</span>}
    </label>
  );
}

function Field({ label, required, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <Label required={required}>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="field"
      />
    </div>
  );
}

function ConfirmationPanel({ data, onClose }) {
  const type = TYPES.find((t) => t.id === data.type);
  return (
    <div
      className="mt-12 bg-parchment-50 border border-parchment-400 p-10 md:p-14 text-center max-w-3xl mx-auto"
      data-testid="appt-confirmation"
    >
      <CheckCircle2 className="mx-auto text-sage-dark" size={56} strokeWidth={1.2} />
      <h3 className="font-serif text-3xl md:text-4xl text-cocoa-900 mt-6">
        Your request has been
        <span className="italic text-terracotta"> received.</span>
      </h3>
      <p className="mt-4 font-sans text-cocoa-700 max-w-xl mx-auto leading-relaxed">
        Thank you, {data.full_name}. The Parish Office will contact you to verify your details
        and confirm your appointment. Please prepare the required documents in the meantime.
      </p>

      <dl className="mt-10 grid sm:grid-cols-2 gap-5 max-w-xl mx-auto text-left">
        <Detail label="Service" value={type?.label || data.type} />
        <Detail label="Preferred Date" value={data.preferred_date} />
        <Detail label="Preferred Time" value={data.preferred_time || "—"} />
        <Detail label="Contact" value={data.contact} />
        <Detail label="Reference" value={data.id.slice(0, 8).toUpperCase()} />
        <Detail label="Status" value="Pending verification" />
      </dl>

      <div className="mt-10 pt-6 border-t border-parchment-400">
        <div className="text-[11px] uppercase tracking-[0.28em] text-sage-dark mb-2 font-sans">
          Parish Office
        </div>
        <a
          href="tel:+639155811419"
          className="font-serif text-2xl text-cocoa-900 hover:text-terracotta"
        >
          0915 581 1419
        </a>
        <div className="mt-1 text-sm text-cocoa-700">sfap2028@gmail.com</div>
      </div>

      <button
        onClick={onClose}
        className="mt-10 inline-flex items-center gap-3 border border-cocoa-900/40 text-cocoa-900 px-7 py-3 font-sans text-sm tracking-wide hover:bg-cocoa-900 hover:text-parchment-100 transition-colors"
      >
        Submit another request
      </button>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.22em] text-sage-dark font-sans">{label}</dt>
      <dd className="font-serif text-lg text-cocoa-900 mt-1 leading-snug">{value}</dd>
    </div>
  );
}

const STATUS_LABEL = {
  pending: "Pending verification",
  confirmed: "Confirmed",
  rescheduled: "Rescheduled",
  completed: "Completed",
  cancelled: "Cancelled",
};
const STATUS_COLOR = {
  pending: "text-terracotta",
  confirmed: "text-sage-dark",
  rescheduled: "text-gold",
  completed: "text-cocoa-700",
  cancelled: "text-cocoa-700/60",
};
const TYPE_LABEL = {
  pastoral_interview: "Pastoral Interview",
  holy_mass: "Holy Mass",
  baptism: "Baptism",
  wedding: "Wedding",
  anointing: "Anointing of the Sick",
  funeral: "Funeral Mass",
  blessing: "Blessing",
  other: "Other Concern",
};

function StatusLookup() {
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState("");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!ref.trim() || !contact.trim()) {
      toast.error("Please enter both your reference number and contact.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/appointments/lookup", { ref: ref.trim(), contact: contact.trim() });
      setResult(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) toast.error("Contact number does not match this reference.");
      else if (status === 404) toast.error("No appointment found with that reference.");
      else toast.error("Could not load. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-10 max-w-3xl mx-auto">
      <div className="text-center mb-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="appt-lookup-toggle"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-cocoa-800 hover:text-terracotta font-sans"
        >
          ✉ Already requested? Check the parish reply
          <span className={`transition-transform ${open ? "rotate-180" : ""}`}>↓</span>
        </button>
      </div>

      {open && (
        <div className="bg-parchment-100 border border-parchment-400 p-6 md:p-8" data-testid="appt-lookup-panel">
          {!result ? (
            <form onSubmit={submit} className="grid sm:grid-cols-12 gap-4 items-end">
              <div className="sm:col-span-5">
                <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-1">
                  Reference Number
                </label>
                <input
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  placeholder="e.g. 78F397F9"
                  data-testid="appt-lookup-ref"
                  className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-2 font-mono text-cocoa-900 focus:outline-none uppercase"
                />
              </div>
              <div className="sm:col-span-4">
                <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-1">
                  Contact Number
                </label>
                <input
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="09XX XXX XXXX"
                  data-testid="appt-lookup-contact"
                  className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-2 font-sans text-cocoa-900 focus:outline-none"
                />
              </div>
              <div className="sm:col-span-3">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="appt-lookup-submit"
                  className="w-full bg-cocoa-900 text-parchment-100 px-5 py-3 text-sm font-sans hover:bg-terracotta transition-colors disabled:opacity-60"
                >
                  {loading ? "Looking up…" : "Check status"}
                </button>
              </div>
              <p className="sm:col-span-12 text-[11px] uppercase tracking-[0.22em] text-sage-dark mt-1">
                ✦ Your privacy matters. We verify your identity by matching your contact number.
              </p>
            </form>
          ) : (
            <LookupResult data={result} onReset={() => { setResult(null); setRef(""); setContact(""); }} />
          )}
        </div>
      )}
    </div>
  );
}

function LookupResult({ data, onReset }) {
  const massDate =
    data.confirmed_date || data.preferred_date;
  const massTime = data.confirmed_time || data.preferred_time;
  return (
    <div data-testid="appt-lookup-result">
      <div className="flex items-baseline justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta">
            {TYPE_LABEL[data.type] || data.type}
          </div>
          <h4 className="font-serif text-2xl md:text-3xl text-cocoa-900 mt-1">
            {data.full_name}
          </h4>
          <div className="text-[11px] uppercase tracking-[0.24em] text-cocoa-700 mt-1 font-mono">
            REF: {data.id.slice(0, 8).toUpperCase()}
          </div>
        </div>
        <div className={`text-[11px] uppercase tracking-[0.28em] font-medium ${STATUS_COLOR[data.status] || "text-cocoa-700"}`}>
          ● {STATUS_LABEL[data.status] || data.status}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 border-y border-parchment-400 py-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-1">
            {data.confirmed_date ? "Confirmed Date" : "Preferred Date"}
          </div>
          <div className="font-serif text-lg text-cocoa-900">{massDate || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-1">
            {data.confirmed_time ? "Confirmed Time" : "Preferred Time"}
          </div>
          <div className="font-serif text-lg text-cocoa-900">{massTime || "—"}</div>
        </div>
      </div>

      {data.parish_reply ? (
        <div className="mt-6 bg-parchment-50 border-l-2 border-terracotta p-5">
          <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-2 font-sans">
            ✉ Message from the Parish
          </div>
          <p className="font-serif italic text-cocoa-900 leading-relaxed whitespace-pre-wrap">
            &ldquo;{data.parish_reply}&rdquo;
          </p>
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap text-[11px] uppercase tracking-[0.22em] text-cocoa-700">
            <span>— {data.parish_reply_by || "Parish Office"}</span>
            {data.parish_reply_at && (
              <span className="text-sage-dark">
                {new Date(data.parish_reply_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      ) : (
        <p className="mt-6 text-sm font-sans italic text-sage-dark">
          ✦ The Parish Office has not yet posted a reply. You will see their message here as soon as it is sent.
        </p>
      )}

      <div className="mt-6 flex items-center gap-4 flex-wrap text-sm font-sans">
        <a href="tel:+639155811419" className="text-terracotta hover:text-cocoa-900">
          📞 Call parish office
        </a>
        <button onClick={onReset} className="text-cocoa-700 hover:text-terracotta">
          Look up another request
        </button>
      </div>
    </div>
  );
}
